/**
 * @fileoverview Custom Hook - Approved Budgets Data
 *
 * Fetches approved baseline budgets from Survey, Drilling, and Recording areas
 * via TanStack Query (useApprovedBaselines).
 */

import { useCallback, useMemo } from 'react';
import { useApprovedBaselines } from '../../../../hooks/useBudgetQueries';

const useApprovedBudgets = (projectId, autoRefresh = true) => {
  const {
    survey: approvedSurvey,
    drilling: approvedDrilling,
    recording: approvedRecording,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useApprovedBaselines(projectId, { enabled: autoRefresh && !!projectId });

  const extractRevenueData = useCallback((budget) => {
    if (!budget || !budget.budget_summary) return null;

    const summary = budget.budget_summary;
    let dailyRevenue = 0;
    let operationalDays = 0;
    let totalRevenue = 0;

    if (summary.resourcePhases && Array.isArray(summary.resourcePhases)) {
      const productionPhase = summary.resourcePhases.find(
        (phase) =>
          phase.phaseType === 'Production' ||
          phase.name === 'Production' ||
          phase.name === 'Operations',
      );
      operationalDays = productionPhase?.duration || 0;

      if (operationalDays === 0) {
        operationalDays = summary.resourcePhases.reduce(
          (sum, phase) => sum + (phase.duration || 0),
          0,
        );
      }
    }

    if (operationalDays === 0) {
      operationalDays =
        summary.operationalDays ||
        summary.operational_days ||
        summary.productionDays ||
        summary.production_days ||
        summary.duration ||
        0;
    }

    totalRevenue =
      summary.total_revenue ||
      summary.totalRevenue ||
      summary.total_project_revenue ||
      summary.totalProjectRevenue ||
      0;

    if (totalRevenue > 0 && operationalDays > 0) {
      dailyRevenue = totalRevenue / operationalDays;
    } else if (operationalDays > 0) {
      const totalPersonnelCost =
        summary.totalProjectPersonnelCost || summary.total_personnel_cost || 0;
      const totalAssetCost = summary.totalProjectAssetCost || summary.total_asset_cost || 0;
      const totalCost = totalPersonnelCost + totalAssetCost;

      if (totalCost > 0) {
        totalRevenue = totalCost;
        dailyRevenue = totalRevenue / operationalDays;
      }
    }

    return {
      budget_id: budget.budget_id,
      budget_name: budget.budget_name,
      budget_type: budget.budget_type,
      dailyRevenue: Math.round(dailyRevenue * 100) / 100,
      operationalDays,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      status: budget.status,
      approved_date: budget.approved_date || null,
    };
  }, []);

  const calculateTotals = useCallback((survey, drilling, recording) => {
    const dailyRevenue =
      (survey?.dailyRevenue || 0) + (drilling?.dailyRevenue || 0) + (recording?.dailyRevenue || 0);

    const totalRevenue =
      (survey?.totalRevenue || 0) + (drilling?.totalRevenue || 0) + (recording?.totalRevenue || 0);

    const approvedCount = [survey, drilling, recording].filter(Boolean).length;

    return {
      dailyRevenue: Math.round(dailyRevenue * 100) / 100,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      allAreasApproved: approvedCount >= 1,
      approvedCount,
    };
  }, []);

  const approvedPresence = useMemo(
    () => ({
      survey: Boolean(approvedSurvey),
      drilling: Boolean(approvedDrilling),
      recording: Boolean(approvedRecording),
    }),
    [approvedSurvey, approvedDrilling, approvedRecording],
  );

  const budgetsData = useMemo(() => {
    const survey = approvedSurvey ? extractRevenueData(approvedSurvey) : null;
    const drilling = approvedDrilling ? extractRevenueData(approvedDrilling) : null;
    const recording = approvedRecording ? extractRevenueData(approvedRecording) : null;
    const totals = calculateTotals(survey, drilling, recording);

    return { survey, drilling, recording, totals };
  }, [approvedSurvey, approvedDrilling, approvedRecording, extractRevenueData, calculateTotals]);

  const error = queryError
    ? queryError.response?.data?.detail || queryError.message || 'Failed to fetch approved budgets'
    : null;

  return {
    survey: budgetsData.survey,
    drilling: budgetsData.drilling,
    recording: budgetsData.recording,
    totals: budgetsData.totals,
    loading,
    error,
    refetch,
    hasAllApprovedBudgets: budgetsData.totals.allAreasApproved,
    hasAnyApprovedBudget: Boolean(
      budgetsData.survey || budgetsData.drilling || budgetsData.recording,
    ),
    approvedCount: budgetsData.totals.approvedCount || 0,
    approvedBudgetsList: [
      budgetsData.survey && 'Survey',
      budgetsData.drilling && 'Drilling',
      budgetsData.recording && 'Recording',
    ].filter(Boolean),
    missingBudgets: [
      !budgetsData.survey && 'Survey',
      !budgetsData.drilling && 'Drilling',
      !budgetsData.recording && 'Recording',
    ].filter(Boolean),
    approvedPresence,
  };
};

export default useApprovedBudgets;
